import type { DealFixedRateLoanData } from '@src/types/OTC';
import {
  isFixedRateLoanSecurity,
  LoanQuoteTerminationTypeEnum,
  logger,
  PricingModeEnum,
  useSecuritiesContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { InspectorText } from './InspectorText';
import { LoanAmountDisplay } from './LoanAmountDisplay';
import { PricingModeDisplay } from './PricingModeDisplay';
import { FixedRateLoanDisplay } from './RateDisplay';
import {
  buildLoanAssetDealInformationFields,
  buildLoanPaymentTypeDealInformationField,
} from './shared/buildLoanDealInformationFields';
import { buildLoanScheduleDealInformationFields } from './shared/buildLoanScheduleDealInformationFields';
import { InspectorSectionsRenderer } from './shared/InspectorSectionsRenderer';
import type { DisplayOutputItem } from './shared/types';
import { useCounterpartyInformation } from './shared/useCounterpartyInformation';
import { useTimingInformation } from './shared/useTimingInformation';
import { SpreadDisplay } from './SpreadDisplay';

const useFixedRateLoanTradeInformation = (instrumentData: DealFixedRateLoanData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isFixedRateLoanSecurity(dealSecurity) ? dealSecurity : undefined;
  if (!resolvedDealSecurity) {
    logger.error(
      new Error(`Failed to get fixed-rate loan deal security for symbol ${instrumentData.Symbol ?? '(missing)'}`),
      {
        extra: {
          dealSecurity,
          resolvedDealSecurity,
        },
      }
    );
    return {
      sectionTitle: 'Deal Information',
      fields: [],
    };
  }

  const termType = resolvedDealSecurity.ProductDetails.TermType;
  const termTypeLabel = termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Open' : 'Fixed';

  return {
    sectionTitle: 'Deal Information',
    fields: compact([
      {
        fieldName: 'Term Type',
        fieldValue: <InspectorText>{termTypeLabel}</InspectorText>,
      },
      {
        fieldName: termType === LoanQuoteTerminationTypeEnum.OpenTerm ? 'Initial Term' : 'Term',
        fieldValue: (
          <InspectorText>
            {termType === LoanQuoteTerminationTypeEnum.OpenTerm
              ? resolvedDealSecurity.ProductDetails.InitialFixedTerm
              : resolvedDealSecurity.ProductDetails.Term}
          </InspectorText>
        ),
      },
      {
        fieldName: 'Side',
        fieldValue: <InspectorText>{instrumentData.DealInfo.Side}</InspectorText>,
      },
      ...buildLoanAssetDealInformationFields(resolvedDealSecurity.ProductDetails),
      {
        fieldName: 'Loan Amount',
        fieldValue: <LoanAmountDisplay instrumentData={instrumentData} dealSecurity={resolvedDealSecurity} />,
      },
      {
        fieldName: 'Pricing Mode',
        fieldValue: instrumentData.DealInfo.PricingMode ? (
          <PricingModeDisplay pricingMode={instrumentData.DealInfo.PricingMode} />
        ) : (
          <InspectorText>-</InspectorText>
        ),
      },
      instrumentData.DealInfo.PricingMode === PricingModeEnum.SpreadToFixing
        ? {
            fieldName: 'Rate Source',
            fieldValue: <InspectorText>{instrumentData.DealInfo.RateSource}</InspectorText>,
          }
        : null,
      instrumentData.DealInfo.PricingMode === PricingModeEnum.SpreadToFixing
        ? {
            fieldName: 'Spread',
            fieldValue: <SpreadDisplay instrumentData={instrumentData} />,
          }
        : null,
      {
        fieldName: 'Rate',
        fieldValue: <FixedRateLoanDisplay instrumentData={instrumentData} />,
      },
      buildLoanPaymentTypeDealInformationField(resolvedDealSecurity.ProductDetails),
      ...buildLoanScheduleDealInformationFields(resolvedDealSecurity.ProductDetails),
    ]),
  };
};

interface FixedRateLoanInstrumentInspectorSectionsProps {
  instrumentData: DealFixedRateLoanData;
}

export const FixedRateLoanInstrumentInspectorSections = ({
  instrumentData,
}: FixedRateLoanInstrumentInspectorSectionsProps) => {
  const tradeInfo = useFixedRateLoanTradeInformation(instrumentData);
  const counterpartyInfo = useCounterpartyInformation(instrumentData);
  const timingInfo = useTimingInformation(instrumentData);

  const sections = [tradeInfo, counterpartyInfo, timingInfo];

  return <InspectorSectionsRenderer sections={sections} />;
};
